import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Archive } from '../api/archives';
import { getItem, type PandoraItemDetail } from '../api/pandora';
import { mediaUrl, timelineStripUrls, TIMELINE_TILE_SECONDS } from '../engine/mediaurls';
import { concatTime, partOffsetsFromDurations } from '../engine/partoffsets';
import { formatTime } from '../format/helpers';
import { useKeyBindings, useKeyScope } from '../keys/useKeyScope';
import type { PandoraVideoSource } from '../format/types';
import { saveToBin } from '../state/bin';
import { usePlayback } from '../state/playback';
import { useTimeline } from '../state/timeline';
import ItemTextPanel from './ItemTextPanel';
import './ItemDetail.css';

/** in/out preset in CONCATENATED item time (from a Moments result). */
export interface ItemPreset {
  concatIn: number;
  concatOut: number;
}

interface Props {
  archive: Archive;
  itemId: string;
  preset?: ItemPreset;
  onBack: () => void;
}

export default function ItemDetail({ archive, itemId, preset, onBack }: Props) {
  const [item, setItem] = useState<PandoraItemDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [part, setPart] = useState(1);
  const [inPoint, setInPoint] = useState<number | null>(null);
  const [outPoint, setOutPoint] = useState<number | null>(null);
  const [videoTime, setVideoTime] = useState(0);
  const [notStreamable, setNotStreamable] = useState(false);
  const [added, setAdded] = useState(false);
  const [binned, setBinned] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pendingSeek = useRef<number | null>(null);

  const durations = useMemo(
    () => item?.durations ?? (item?.duration ? [item.duration] : []),
    [item],
  );
  const partOffsets = useMemo(() => partOffsetsFromDurations(durations), [durations]);
  const totalDur = durations.reduce((a, b) => a + b, 0);
  const partDur = durations[part - 1] ?? item?.duration ?? 0;

  useEffect(() => {
    let alive = true;
    setItem(null);
    setLoadError(null);
    setNotStreamable(false);
    getItem(archive.site, itemId).then(
      (it) => {
        if (!alive) return;
        setItem(it);
      },
      (e) => alive && setLoadError(e instanceof Error ? e.message : 'failed to load item'),
    );
    return () => {
      alive = false;
    };
  }, [archive.site, itemId]);

  // apply preset (concat time → part + part-relative in/out) once metadata is in
  useEffect(() => {
    if (!item || !preset) return;
    const durs = item.durations ?? (item.duration ? [item.duration] : []);
    let acc = 0;
    for (let p = 0; p < durs.length; p++) {
      if (preset.concatIn < acc + durs[p] || p === durs.length - 1) {
        setPart(p + 1);
        const i = Math.max(0, preset.concatIn - acc);
        setInPoint(i);
        setOutPoint(Math.min(Math.max(preset.concatOut - acc, i + 0.5), durs[p]));
        pendingSeek.current = i;
        break;
      }
      acc += durs[p];
    }
  }, [item, preset]);

  const videoUrl = mediaUrl(archive, itemId, part);

  const onLoadedMetadata = useCallback(() => {
    if (pendingSeek.current !== null && videoRef.current) {
      videoRef.current.currentTime = pendingSeek.current;
      pendingSeek.current = null;
    }
  }, []);

  // I / O keyboard shortcuts while the detail view is open (clip-viewer scope
  // shadows any timeline-level I/O bindings while this view is mounted)
  useKeyScope('clip-viewer');
  useKeyBindings(() => [
    {
      id: 'clipViewer.markIn',
      keys: ['I'],
      scope: 'clip-viewer',
      group: 'Clip viewer',
      description: 'Mark in point',
      run: () => {
        const t = videoRef.current?.currentTime ?? 0;
        setInPoint(t);
        setOutPoint((o) => (o !== null && o <= t ? null : o));
      },
    },
    {
      id: 'clipViewer.markOut',
      keys: ['O'],
      scope: 'clip-viewer',
      group: 'Clip viewer',
      description: 'Mark out point',
      run: () => {
        const t = videoRef.current?.currentTime ?? 0;
        setOutPoint(t);
        setInPoint((i) => (i !== null && i >= t ? null : i));
      },
    },
  ]);

  function switchPart(p: number) {
    setPart(p);
    setNotStreamable(false);
    setInPoint(null);
    setOutPoint(null);
    setVideoTime(0);
  }

  function scrubTo(concatT: number) {
    // find part containing concatT
    for (let p = durations.length - 1; p >= 0; p--) {
      if (concatT >= partOffsets[p]) {
        const local = concatT - partOffsets[p];
        if (p + 1 !== part) {
          setPart(p + 1);
          setNotStreamable(false);
          pendingSeek.current = local;
        } else if (videoRef.current) {
          videoRef.current.currentTime = local;
        }
        return;
      }
    }
  }

  function addToTimeline(asAudio = false) {
    if (!item) return;
    const i = inPoint ?? 0;
    const o = outPoint ?? partDur;
    if (o - i < 0.05) return;
    const source: PandoraVideoSource = {
      kind: 'pandora-video',
      archive: archive.key,
      item: itemId,
      part,
      duration: partDur,
      title: item.title || itemId,
      posterFrame: item.posterFrame ?? partDur / 2,
      url: null,
    };
    const tl = useTimeline.getState();
    const at = usePlayback.getState().currentTime;
    if (asAudio) tl.addAudioClip(source, i, o, at);
    else tl.addVideoClip(source, i, o, at);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  }

  function saveClipToBin() {
    if (!item) return;
    const marked = inPoint !== null || outPoint !== null;
    saveToBin({
      archive: archive.key,
      item: itemId,
      title: item.title || itemId,
      duration: totalDur,
      parts: item.parts ?? 1,
      ...(item.posterFrame != null ? { posterFrame: item.posterFrame } : {}),
      ...(marked
        ? {
            inPoint: concatTime(partOffsets, part, inPoint ?? 0),
            outPoint: concatTime(partOffsets, part, outPoint ?? partDur),
          }
        : {}),
    });
    setBinned(true);
    setTimeout(() => setBinned(false), 1200);
  }

  const stripUrls = useMemo(
    () => (totalDur > 0 ? timelineStripUrls(archive, itemId, totalDur) : []),
    [archive, itemId, totalDur],
  );
  const concatNow = (partOffsets[part - 1] ?? 0) + videoTime;

  return (
    <div className="item-detail">
      <div className="item-detail-head">
        <button className="ghost" onClick={onBack}>
          ← results
        </button>
        <a
          className="item-detail-link"
          href={`${archive.site}/${itemId}`}
          target="_blank"
          rel="noreferrer"
        >
          {archive.name} ↗
        </a>
      </div>

      {loadError && <div className="item-detail-error">{loadError}</div>}
      {!item && !loadError && <div className="search-hint">loading…</div>}

      {item && (
        <>
          <h3 className="item-detail-title">{item.title || itemId}</h3>
          <div className="item-detail-meta">
            {[item.director?.join(', '), item.year, formatTime(totalDur)]
              .filter(Boolean)
              .join(' · ')}
          </div>

          {notStreamable ? (
            <div className="item-not-streamable">
              <p>not streamable</p>
              <span>
                this item's media is not publicly available (rights level {item.rightslevel})
              </span>
            </div>
          ) : (
            <video
              key={`${itemId}-${part}`}
              ref={videoRef}
              className="item-detail-video"
              src={videoUrl}
              controls
              playsInline
              crossOrigin="anonymous"
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={(e) => setVideoTime(e.currentTarget.currentTime)}
              onError={() => setNotStreamable(true)}
            />
          )}

          {stripUrls.length > 0 && (
            <div
              className="item-strip"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                scrubTo(((e.clientX - rect.left) / rect.width) * totalDur);
              }}
            >
              {stripUrls.map((url, i) => {
                const tileDur = Math.min(TIMELINE_TILE_SECONDS, totalDur - i * TIMELINE_TILE_SECONDS);
                return (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    draggable={false}
                    style={{ width: `${(tileDur / totalDur) * 100}%` }}
                  />
                );
              })}
              <div
                className="item-strip-cursor"
                style={{ left: `${Math.min(100, (concatNow / totalDur) * 100)}%` }}
              />
              {inPoint !== null && (
                <div
                  className="item-strip-range"
                  style={{
                    left: `${(((partOffsets[part - 1] ?? 0) + inPoint) / totalDur) * 100}%`,
                    width: `${
                      ((Math.max(0, (outPoint ?? videoTime) - inPoint)) / totalDur) * 100
                    }%`,
                  }}
                />
              )}
            </div>
          )}

          {item.parts > 1 && (
            <div className="item-parts">
              part:
              {durations.map((_, i) => (
                <button
                  key={i}
                  className={part === i + 1 ? 'active' : ''}
                  onClick={() => switchPart(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}

          <div className="item-marks">
            <button onClick={() => setInPoint(videoTime)} title="Set in point (I)">
              In <span className="timecode">{inPoint !== null ? formatTime(inPoint, true) : '—'}</span>
            </button>
            <button onClick={() => setOutPoint(videoTime)} title="Set out point (O)">
              Out{' '}
              <span className="timecode">
                {outPoint !== null ? formatTime(outPoint, true) : '—'}
              </span>
            </button>
            <span className="timecode item-marks-dur">
              {formatTime(Math.max(0, (outPoint ?? partDur) - (inPoint ?? 0)), true)}
            </span>
          </div>

          <div className="item-add-row">
            <button
              className="primary item-add"
              disabled={notStreamable || (outPoint ?? partDur) - (inPoint ?? 0) < 0.05}
              onClick={() => addToTimeline(false)}
            >
              {added ? 'Added ✓' : 'Add to timeline'}
            </button>
            <button
              className="item-add"
              title="Add only this range's audio to the audio track"
              disabled={notStreamable || (outPoint ?? partDur) - (inPoint ?? 0) < 0.05}
              onClick={() => addToTimeline(true)}
            >
              + Audio
            </button>
            <button
              className="item-add"
              title="Save this item (with current marks) to the working bin"
              onClick={saveClipToBin}
            >
              {binned ? 'Saved ✓' : 'Save to bin'}
            </button>
          </div>

          <ItemTextPanel archive={archive} itemId={itemId} partOffsets={partOffsets} />
        </>
      )}
    </div>
  );
}
